import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Thin React wrapper around the browser-native Web Speech API.
 *
 * No npm dependency, no model download — Chrome, Edge, Brave and Safari
 * ship this. It supports many languages out of the box including
 * `en-IN` (Indian English) and `hi-IN` (Hindi).
 *
 * Usage:
 *   const { supported, listening, transcript, start, stop } =
 *     useSpeechRecognition({ lang: 'en-IN', onFinal: (text) => ... });
 *
 *   <button onClick={listening ? stop : start} disabled={!supported}>
 *     {listening ? 'Stop' : 'Speak'}
 *   </button>
 *
 * Notes:
 *   - Some browsers (Chrome) send audio to a Google service for
 *     transcription. If you need true on-device STT later, swap this
 *     hook for a Whisper-based one — the rest of the panel UI doesn't
 *     need to change.
 */

const getRecognitionCtor = () => {
	if (typeof window === 'undefined') return null;
	return (
		window.SpeechRecognition ||
		window.webkitSpeechRecognition ||
		null
	);
};

export const isSpeechRecognitionSupported = () => Boolean(getRecognitionCtor());

/**
 * Best-effort speech language picker.
 *
 * The Web Speech API has no real "auto" mode — you must set a single
 * language code per recognition. This helper looks at the user's browser
 * locale and returns the closest Indian variant:
 *   - Hindi browser ........... -> hi-IN
 *   - Indian English browser .. -> en-IN
 *   - Any other English ....... -> en-IN (Indian English still works well
 *                                          for global English speakers)
 *   - Anything else / unknown . -> the fallback you pass in
 *
 * For HFCL's audience (predominantly Indian users) en-IN also handles
 * Hinglish-style code-switching reasonably well, and Ollama on the
 * backend understands both Hindi and English text so the chat routes
 * correctly either way.
 *
 * @param {string} fallback default lang when nothing matches
 * @return {string} BCP-47 language tag
 */
export const detectSpeechLang = (fallback = 'en-IN') => {
	if (typeof navigator === 'undefined') return fallback;
	const candidates = []
		.concat(navigator.language || [])
		.concat(Array.isArray(navigator.languages) ? navigator.languages : []);
	for (const raw of candidates) {
		const code = String(raw || '').toLowerCase();
		if (!code) continue;
		if (code.startsWith('hi')) return 'hi-IN';
		if (code.startsWith('en')) return 'en-IN';
	}
	return fallback;
};

export const useSpeechRecognition = ({
	lang = 'en-IN',
	interimResults = true,
	onFinal,
	onError,
} = {}) => {
	const Ctor = getRecognitionCtor();
	const recRef = useRef(null);
	const finalCbRef = useRef(onFinal);
	const errorCbRef = useRef(onError);

	const [listening, setListening] = useState(false);
	const [transcript, setTranscript] = useState('');
	const [error, setError] = useState(null);

	// Keep refs to the latest callbacks so we don't have to recreate the
	// recogniser whenever the parent re-renders.
	useEffect(() => { finalCbRef.current = onFinal; }, [onFinal]);
	useEffect(() => { errorCbRef.current = onError; }, [onError]);

	// (Re)create the recogniser when language changes.
	useEffect(() => {
		if (!Ctor) return undefined;

		const rec = new Ctor();
		rec.continuous = false;
		rec.interimResults = interimResults;
		rec.lang = lang;
		rec.maxAlternatives = 1;

		rec.onresult = (event) => {
			let finalText = '';
			let interim = '';
			for (let i = event.resultIndex; i < event.results.length; i += 1) {
				const r = event.results[i];
				if (r.isFinal) finalText += r[0].transcript;
				else interim += r[0].transcript;
			}
			const liveText = (finalText || interim || '').trim();
			setTranscript(liveText);
			if (finalText && typeof finalCbRef.current === 'function') {
				finalCbRef.current(finalText.trim());
			}
		};

		rec.onerror = (e) => {
			const code = e?.error || 'error';
			setError(code);
			setListening(false);
			if (typeof errorCbRef.current === 'function') {
				errorCbRef.current(code);
			}
		};

		rec.onend = () => {
			setListening(false);
		};

		recRef.current = rec;
		return () => {
			try { rec.stop(); } catch (_) { /* ignore */ }
			recRef.current = null;
		};
	}, [Ctor, lang, interimResults]);

	const start = useCallback(() => {
		const rec = recRef.current;
		if (!rec) return;
		setTranscript('');
		setError(null);
		try {
			rec.start();
			setListening(true);
		} catch (_) {
			// Most common case: start() called twice without a stop in between.
			try { rec.stop(); } catch (__) { /* ignore */ }
			setListening(false);
		}
	}, []);

	const stop = useCallback(() => {
		const rec = recRef.current;
		if (!rec) return;
		try { rec.stop(); } catch (_) { /* ignore */ }
		setListening(false);
	}, []);

	return {
		supported: Boolean(Ctor),
		listening,
		transcript,
		error,
		start,
		stop,
	};
};
